/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  shopProductHasAvailability,
  cartProductHasAvailability,
  cartProductHasAvailabilityData,
  cartProductHasQuantitiesEntered,
  isCartProductPaymentMethodValid,
  isCartProductPurchasable,
  isShopProductExpired,
  isValueMultipleOf,
  SHIPPING_METHOD_STANDARD
} from '../constants';
import {
  ERROR_ITEM_EXPIRED,
  ERROR_ITEM_FUNDAMENTAL_RULE_EXPIRED,
  ERROR_ITEM_PAYMENT_METHOD,
  ERROR_ITEM_PRE_PACK,
  ERROR_ITEM_QUANTITY_EXCEEDED,
  ERROR_ITEM_MAX_QUANTITY_EXCEEDED,
  ERROR_FREE_ITEM_MAX_QUANTITY_EXCEEDED,
  ERROR_ITEM_UNIT_ALLOTMENT_EXCEEDED,
  ERROR_PRODUCT_AMOUNT_ORDERABLE_EXCEEDED,
  ERROR_ITEM_QUANTITY_NONE,
  ERROR_ITEM_UNAVAILABLE,
  ERROR_ITEM_MIN_RULE,
  ERROR_PRODUCT_GROUP_NO_ADDRESS,
  ERROR_FIELD_EMPTY,
  ERROR_FIELD_INVALID,
  ERROR_MAP,
  ERROR_DATA_PHONENUM,
  ERROR_DATA_PHONENUM_EXT,
  ERROR_DATA_PURCHASE_ORDER_NUM,
  ERROR_DATA_SHIPPING_METHOD,
  ERROR_DATA_NOTES_TO_NIKE,
  ERROR_CART_EMPTY,
  ERROR_CART_NEGATIVE_COMP_BALANCE,
  errorIsStandAlone
} from './errors';
import { RULETYPE_PREPACK } from './itemRules';
import { ADDON_CART_VALIDATION } from '../../util/actionAddons';
import { getTotalAvailability } from '../../util/mapAvailability';
import { isNASalesOrg, isEUSalesOrg } from '../../util/constants';
import {
  ORDER_TYPE_ATONCE,
  ORDER_TYPE_FORECAST,
  ORDER_TYPE_FUTURES,
  PAYMENT_COMP,
  PAYMENT_UNIT
} from '../../shop/constants';
import { findProductInfo } from '../../productsInfo';

import { groupBy, isEmpty as isEmptyLodash, isEqual, sortBy } from '../../util/lodash';

/*
  TODO:
    A lot of these constants up here should eventually be extracted out to a more general util
    location as we move the site's field validation away from util/formValidations.jsx and get
    error messages left-over there into our translation files.
*/

export const MIN_PHONE_NUMBER = 8;
export const MAX_PHONE_NUMBER = 18;
export const MAX_PHONE_NUMBER_EXT = 30;
export const MAX_PO_NUMBER = 35;
export const MAX_NOTES_TO_NIKE = 70;

// Field-specific validations
export const isPurchaseOrderNumber = val =>
  // eslint-disable-next-line
  val && /^[\p{L}\p{N}\s]*$/u.test(val);

// Is {val} a number
export const isNumber = val => /^[0-9]*$/.test(val);

// Is {val} a phone number (contains only numbers & dash)
export const isBasicPhoneNumber = val => /^[\d./-]+$/.test(val);

// Is {val} empty
export const isEmpty = val => !(val && val !== '');

// Min/Max length validation creators
export const isMinLength = min => val => val.length >= min;
export const isMaxLength = max => val => val.length <= max;

export const buildError = (type, data = {}) => ({ type, data });

const validateCartProduct = (action, item) => {
  const itemErrors = [];
  const {
    productAvailability,
    selectedDepartment,
    useCurrentContract,
    accountIsBillable,
    allotments: { unitAllotments },
    productsInfoList
  } = action.addon[ADDON_CART_VALIDATION];
  const productInfo = findProductInfo(productsInfoList, item.productCode);
  const { type: departmentType } = selectedDepartment;
  const isAtOnce = departmentType === ORDER_TYPE_ATONCE;
  const isFutures = departmentType === ORDER_TYPE_FUTURES;
  const quantitiesEntered = cartProductHasQuantitiesEntered(item);

  // Product expired?
  if (isShopProductExpired(item)) itemErrors.push(buildError(ERROR_ITEM_EXPIRED));

  // Item purchasable?
  if (!isCartProductPurchasable(item)) itemErrors.push(buildError(ERROR_ITEM_UNAVAILABLE));

  // At Once Only Errors
  if (isAtOnce) {
    const hasAvailabilityData = cartProductHasAvailabilityData(item, productAvailability);

    if (
      !hasAvailabilityData ||
      !productInfo ||
      !shopProductHasAvailability(productInfo, productAvailability) ||
      !cartProductHasAvailability(item, productAvailability)
    )
      itemErrors.push(buildError(ERROR_ITEM_UNAVAILABLE));
  }

  // At Once or Future Errors
  if (isAtOnce || isFutures) {
    if (
      !isCartProductPaymentMethodValid(
        item,
        productsInfoList,
        selectedDepartment,
        useCurrentContract,
        accountIsBillable,
        unitAllotments
      )
    )
      itemErrors.push(buildError(ERROR_ITEM_PAYMENT_METHOD));
  }

  // Does Item have quantities entered?
  if (!quantitiesEntered) {
    // Only show no quantity error if there is not already other specific errors
    if (!itemErrors.some(error => [ERROR_ITEM_UNAVAILABLE].includes(error.type)))
      itemErrors.push(buildError(ERROR_ITEM_QUANTITY_NONE));
  } else {
    // Does Item Adhere to PrePack Validation Rules?
    const ruleSource = productInfo || item;
    const rule = ruleSource.rules.find(rule => rule.type === RULETYPE_PREPACK);
    const invalidPrePackSizes = rule
      ? item.sizes.filter(size => !isValueMultipleOf(size.quantity, rule.data.value))
      : [];

    if (invalidPrePackSizes.length > 0) {
      itemErrors.push(
        buildError(ERROR_ITEM_PRE_PACK, {
          count: rule.data.value,
          invalidSizes: invalidPrePackSizes.map(size => size.sizeCode)
        })
      );
    }
  }

  if (!isEqual(item.errors, itemErrors)) item.errors = itemErrors;

  return item;
};

const reduceSizeQuantities = group =>
  group.reduce(
    (totals, item) => {
      totals.unitCount += item.totals.unitCount;
      item.sizes.forEach(size => {
        totals[size.sizeCode] = (totals[size.sizeCode] || 0) + size.quantity;
      });

      return totals;
    },
    { unitCount: 0 }
  );

export const validateCartProductGroupQuantity = (
  action,
  cartProducts,
  idsToValidate,
  cartProductsMaxAmountsOrderable
) => {
  const { productAvailability, productMinRules, productFundamentalRules, selectedDepartment } = action.addon[
    ADDON_CART_VALIDATION
  ];

  const cartProductFilter = cartProducts.filter(item => idsToValidate.includes(item.id));
  const productCodesToValidate = [...new Set(cartProductFilter.map(item => item.productCode))];
  const cartProductsToValidate = cartProducts.filter(item => productCodesToValidate.includes(item.productCode));
  const productCodeGroups = groupBy(cartProductsToValidate, 'productCode');

  Object.entries(productCodeGroups).forEach(([productCode, group]) => {
    const sizeQuantities = reduceSizeQuantities(group);
    const fundamentalSubgroup = groupBy(
      group.filter(item => !!item.fundamentalRuleId),
      'fundamentalRuleId'
    );

    // Do quantities exceed the available amount or 500 per size? (At Once Only)
    if (selectedDepartment.type === ORDER_TYPE_ATONCE) {
      const availabilityList = productAvailability[productCode];

      if (availabilityList) {
        const availabilityExceeded = [];
        const maxQuantityExceeded = [];
        const maxFreeQuantityExceeded = [];

        Object.keys(sizeQuantities)
          .filter(key => key !== 'unitCount')
          .forEach(desc => {
            const availability = getTotalAvailability(availabilityList[desc]);
            const quantity = sizeQuantities[desc];
            const isContractAvailabilityExists = !!availabilityList[desc]?.CONTRACT;

            if (availability && availability < quantity) {
              availabilityExceeded.push(desc);
            }
            if (availability && quantity > 500 && !isContractAvailabilityExists) {
              maxQuantityExceeded.push(desc);
            }
            if (
              availability &&
              isContractAvailabilityExists &&
              availabilityList[desc].GENERAL > 500 &&
              quantity > availabilityList[desc].CONTRACT + 500
            ) {
              maxFreeQuantityExceeded.push(desc);
            }
          });

        if (availabilityExceeded.length)
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_QUANTITY_EXCEEDED);
            item.errors.push(
              buildError(ERROR_ITEM_QUANTITY_EXCEEDED, {
                invalidSizes: availabilityExceeded
              })
            );
          });
        else
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_QUANTITY_EXCEEDED);
          });

        if (maxQuantityExceeded.length)
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_MAX_QUANTITY_EXCEEDED);
            item.errors.push(
              buildError(ERROR_ITEM_MAX_QUANTITY_EXCEEDED, {
                invalidSizes: maxQuantityExceeded
              })
            );
          });
        else
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_MAX_QUANTITY_EXCEEDED);
          });

        if (maxFreeQuantityExceeded.length)
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_FREE_ITEM_MAX_QUANTITY_EXCEEDED);
            item.errors.push(
              buildError(ERROR_FREE_ITEM_MAX_QUANTITY_EXCEEDED, {
                invalidSizes: maxFreeQuantityExceeded
              })
            );
          });
        else
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_FREE_ITEM_MAX_QUANTITY_EXCEEDED);
          });
      }
    }

    // Does the product group adhere to the minimum quantity per size rule?
    const min = productMinRules && productMinRules[productCode];
    if (typeof min === 'number') {
      const { unitCount } = sizeQuantities;
      if (unitCount > 0 && unitCount < min) {
        group.forEach(item => {
          item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_MIN_RULE);
          // This key must be `amount` for translation purposes
          item.errors.push(
            buildError(ERROR_ITEM_MIN_RULE, {
              amount: min,
              unitCount
            })
          );
        });
      } else {
        group.forEach(item => {
          item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_MIN_RULE);
        });
      }
    }
    // Is there an active fundamental rule?
    // Does the fundamental subgroup adhere to the minimum quantity?
    if (!isEmptyLodash(fundamentalSubgroup) && !isEmptyLodash(productFundamentalRules))
      Object.entries(fundamentalSubgroup).forEach(([fundamentalRuleId, group]) => {
        const { unitCount } = reduceSizeQuantities(group);

        if (!productFundamentalRules[fundamentalRuleId])
          group.forEach(item => {
            item.errors.push(buildError(ERROR_ITEM_FUNDAMENTAL_RULE_EXPIRED));
          });
        else if (unitCount > 0 && unitCount < productFundamentalRules[fundamentalRuleId].minimumQuantity)
          group.forEach(item => {
            item.errors.push(
              buildError(ERROR_ITEM_MIN_RULE, {
                amount: productFundamentalRules[fundamentalRuleId].minimumQuantity,
                unitCount
              })
            );
          });
        else
          group.forEach(item => {
            item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_MIN_RULE);
          });
      });

    // Does the product group have a total quantity less than the max amount orderable?
    const productMax = cartProductsMaxAmountsOrderable && cartProductsMaxAmountsOrderable[productCode];
    if (productMax) {
      const { unitCount } = sizeQuantities;
      if (unitCount > productMax)
        group.forEach(item => {
          item.errors = item.errors.filter(error => error.type !== ERROR_PRODUCT_AMOUNT_ORDERABLE_EXCEEDED);
          item.errors.push(buildError(ERROR_PRODUCT_AMOUNT_ORDERABLE_EXCEEDED));
        });
      else
        group.forEach(item => {
          item.errors = item.errors.filter(error => error.type !== ERROR_PRODUCT_AMOUNT_ORDERABLE_EXCEEDED);
        });
    }
  });
};

const validateCartProductUnitAllotmentTotals = (action, cartProducts, idsToValidate) => {
  const {
    allotments: { unitAllotments }
  } = action.addon[ADDON_CART_VALIDATION];

  const cartProductFilter = cartProducts.filter(
    item => item.paymentMethod === PAYMENT_UNIT && item.unitAllotmentId && idsToValidate.includes(item.id)
  );
  const unitAllotmentIdsToValidate = [...new Set(cartProductFilter.map(item => item.unitAllotmentId))];
  const cartProductsToValidate = cartProducts.filter(item => unitAllotmentIdsToValidate.includes(item.unitAllotmentId));
  const unitAllotmentIdGroups = groupBy(cartProductsToValidate, 'unitAllotmentId');

  Object.entries(unitAllotmentIdGroups).forEach(([unitAllotmentId, group]) => {
    const accountAllotment = unitAllotments.find(
      allotment => `${allotment.unitAttributeName}${allotment.unitAttributeValue}` === unitAllotmentId
    );
    if (!accountAllotment) return; // Short circuit if there's no allotment

    const { allotmentAmount, totalSpend } = accountAllotment;

    // TODO: Locker can give us a negative allotment amount.
    //  Ideally, we should not have to prevent negative available unit math.
    const availableUnits = Math.max(0, Number(allotmentAmount) - Number(totalSpend));
    const totalGroupQuantity = group.reduce((total, item) => total + item.totals.unitCount, 0);
    const remainingUnits = availableUnits - totalGroupQuantity;

    // Clear exceeded allotment errors for the current group
    group.forEach(
      item => (item.errors = item.errors.filter(error => error.type !== ERROR_ITEM_UNIT_ALLOTMENT_EXCEEDED))
    );

    // If over allotment
    if (remainingUnits < 0) {
      // Add error to group items
      group.forEach(item =>
        item.errors.push(
          buildError(ERROR_ITEM_UNIT_ALLOTMENT_EXCEEDED, { availableUnits, totalGroupQuantity, remainingUnits })
        )
      );
    }
  });
};

// Returns the provided errors list sorted by priority-level, high to low
export const prioritizeErrors = errors => sortBy(errors, e => ERROR_MAP[e.type].priority);

// Filters and returns the provided errors list
export const filterErrors = errors =>
  errors.reduce((prev, curr) => (prev.some(e => errorIsStandAlone(e)) ? prev : [...prev, curr]), []);

// Error paylaod builders
const buildMinPayload = (data, min) => ({ ...data, error: { id: 'checkout.error.invalid.min', min } });
const buildMaxPayload = (data, max) => ({ ...data, error: { id: 'checkout.error.invalid.max', max } });
const buildPONumberPayload = data => ({ ...data, error: { id: 'checkout.error.invalid.chars' } });
const buildPhonePayload = data => ({ ...data, error: { id: 'checkout.error.invalid.phone' } });
const buildExtPayload = data => ({ ...data, error: { id: 'checkout.error.invalid.ext' } });

// Common validations
const validateMinLength = (input, min, data, errors) => {
  if (!isMinLength(min)(input)) {
    errors.push(buildError(ERROR_FIELD_INVALID, { ...buildMinPayload(data, min) }));
  }
};
const validateMaxLength = (input, max, data, errors) => {
  if (!isMaxLength(max)(input)) {
    errors.push(buildError(ERROR_FIELD_INVALID, { ...buildMaxPayload(data, max) }));
  }
};

// Empty Cart Validation
export const validateEmptyCart = (cart, errors) => {
  if (cart.products.length === 0) errors.push(buildError(ERROR_CART_EMPTY));
};

// Comp Balance Validation
export const validateCompBalance = (cart, action, errors) => {
  const {
    products,
    totals: { comp }
  } = cart;
  const {
    allotments: { remainingAllotment },
    selectedDepartment: { type }
  } = action.addon[ADDON_CART_VALIDATION];

  const isForecast = type === ORDER_TYPE_FORECAST;
  const totalRemaining = +remainingAllotment - comp;
  const isCompMethodUtilized = products.some(product => product.paymentMethod === PAYMENT_COMP);

  if (!isForecast && totalRemaining < 0 && isCompMethodUtilized)
    errors.push(buildError(ERROR_CART_NEGATIVE_COMP_BALANCE));
  else errors = errors.filter(error => error.type !== ERROR_CART_NEGATIVE_COMP_BALANCE);
};

// Phone Number Validations
export const validatePhoneNumber = (input, errors) => {
  // Is empty?
  if (isEmpty(input)) {
    errors.push(buildError(ERROR_FIELD_EMPTY, ERROR_DATA_PHONENUM));
  } else {
    // Min/Max length
    validateMinLength(input, MIN_PHONE_NUMBER, ERROR_DATA_PHONENUM, errors);
    validateMaxLength(input, MAX_PHONE_NUMBER, ERROR_DATA_PHONENUM, errors);

    // Only numbers
    if (!isBasicPhoneNumber(input)) {
      errors.push(buildError(ERROR_FIELD_INVALID, { ...buildPhonePayload(ERROR_DATA_PHONENUM) }));
    }
  }
};

// Phone Number Ext Validations
export const validatePhoneNumberExt = (input, errors) => {
  if (!isEmpty(input)) {
    // Max length
    validateMaxLength(input, MAX_PHONE_NUMBER_EXT, ERROR_DATA_PHONENUM_EXT, errors);

    // Only numbers
    if (!isNumber(input)) errors.push(buildError(ERROR_FIELD_INVALID, { ...buildExtPayload(ERROR_DATA_PHONENUM_EXT) }));
  }
};

// Purchase Order Number Validations
export const validatePurchaseOrderNumber = (input, errors) => {
  // Is empty?
  if (isEmpty(input?.trim())) errors.push(buildError(ERROR_FIELD_EMPTY, ERROR_DATA_PURCHASE_ORDER_NUM));
  else {
    // Max length
    validateMaxLength(input, MAX_PO_NUMBER, ERROR_DATA_PURCHASE_ORDER_NUM, errors);

    // Is legit purchase order number?
    if (!isPurchaseOrderNumber(input))
      errors.push(buildError(ERROR_FIELD_INVALID, { ...buildPONumberPayload(ERROR_DATA_PURCHASE_ORDER_NUM) }));
  }
};

// Shipping Method Validations
export const validateShippingMethod = (input, errors) => {
  // Is empty?
  if (isEmpty(input)) errors.push(buildError(ERROR_FIELD_EMPTY, ERROR_DATA_SHIPPING_METHOD));
};

// Notes-To-Nike Validations
export const validateNotesToNike = (input, shippingMethod, salesOrg, orderType, isBillableOrder, errors) => {
  // Is empty?
  if (isEmpty(input)) {
    // Notes to Nike required if Expedited Shipping unless a NA Billable Order
    if (
      shippingMethod?.toLowerCase() !== SHIPPING_METHOD_STANDARD.toLowerCase() &&
      !isEUSalesOrg(salesOrg) &&
      !(isNASalesOrg(salesOrg) && orderType === ORDER_TYPE_ATONCE && isBillableOrder)
    )
      errors.push(buildError(ERROR_FIELD_EMPTY, ERROR_DATA_NOTES_TO_NIKE));
  } else validateMaxLength(input, MAX_NOTES_TO_NIKE, ERROR_DATA_NOTES_TO_NIKE, errors); // Max length
};

// Validate the current cart's {productGroup}s
export const validateProductGroups = ({ productGroups, headers: { orderType } }, errors) => {
  productGroups.forEach(({ type, cartProductIds, address }) => {
    if (orderType !== ORDER_TYPE_FORECAST) {
      if (cartProductIds.length > 0 && !address) errors.push(buildError(ERROR_PRODUCT_GROUP_NO_ADDRESS, { type }));
    }
  });
};

// Cart items validation
const validateCartProducts = (action, cartProducts, idsToValidate, cartProductsMaxAmountsOrderable) => {
  cartProducts.forEach((item, index) => {
    if (idsToValidate.includes(item.id)) cartProducts[index] = validateCartProduct(action, item);
  });

  validateCartProductGroupQuantity(action, cartProducts, idsToValidate, cartProductsMaxAmountsOrderable);
  validateCartProductUnitAllotmentTotals(action, cartProducts, idsToValidate);

  return cartProducts;
};

// Cart-level validation
const buildCartLevelErrors = (cart, action) => {
  const errors = [];
  const {
    headers: {
      phoneNumber,
      phoneNumberExt,
      shippingMethod,
      purchaseOrderNumber,
      notesToNike,
      orderType,
      salesOrg,
      isBillableOrder
    }
  } = cart;

  // Basic
  validateEmptyCart(cart, errors);

  // Shipping Details
  if (orderType !== ORDER_TYPE_FORECAST) {
    // Comp Balance
    validateCompBalance(cart, action, errors);

    // Shipping Details
    validatePhoneNumber(phoneNumber, errors);
    validatePhoneNumberExt(phoneNumberExt, errors);
    validatePurchaseOrderNumber(purchaseOrderNumber, errors);
    validateShippingMethod(shippingMethod, errors);
    validateNotesToNike(notesToNike, shippingMethod, salesOrg, orderType, isBillableOrder, errors);
  } else {
    validatePhoneNumber(phoneNumber, errors);
    validatePhoneNumberExt(phoneNumberExt, errors);
    validatePurchaseOrderNumber(purchaseOrderNumber, errors);
  }

  // Product Groups
  validateProductGroups(cart, errors);

  return errors;
};

// FULLY validates and returns the provided cart
export const validateCart = (action, cart, cartProductIds = []) => {
  const idsToValidate = cartProductIds.length === 0 ? cart.products.map(item => item.id) : cartProductIds;

  return {
    ...cart,
    errors: buildCartLevelErrors(cart, action),
    products: produce(cart.products, draftProducts =>
      validateCartProducts(action, draftProducts, idsToValidate, cart.productsMaxAmountsOrderable)
    )
  };
};
